jQuery(($) => {
  class CostCalculator {
    constructor(element) {
      this._ = element;
      this._element = $(element);

      this._plans = JSON.parse(this._element.find('#plans').val());

      this._explanationLabel = this._element.find('#explanation-label');
      this._bestPlanLabel = this._element.find('#best-plan-label');
      this._estimatedCostLabel = this._element.find('#estimated-cost-label');

      this._devicesValue = this._element.find('#devices-value');
      this._devicesValueLabel = this._element.find('#devices-value-label');
      this._devicesSlider = new RangeSliderPips({
        target: document.querySelector('#devices-slider'),
        props: {
          values: [1],
          formatter: this.deviceCountFormatter,
          float: true,
          pips: true,
          all: 'label',
          min: 0,
          max: 40,
          pipstep: 10,
          range: 'min',
          springValues: { stiffness: 0.2, damping: 1 },
        },
      });

      this._dataValue = this._element.find('#data-value');
      this._dataValueLabel = this._element.find('#data-value-label');
      this._dataSlider = new RangeSliderPips({
        target: document.querySelector('#data-slider'),
        props: {
          values: [19],
          formatter: this.dataFormatter,
          float: true,
          pips: true,
          all: 'label',
          min: 19,
          max: 60,
          pipstep: 9,
          range: 'min',
          springValues: { stiffness: 0.2, damping: 1 },
        },
      });

      this.init();
    }

    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
    // ✅ Initialize
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

    init() {
      this.setupHandlers();
      this._element.trigger('init', this);

      // Initial values
      this.onDevicesSliderChange({ detail: { value: 1 } });
      this.onDataSliderChange({ detail: { value: 19 } });
    }

    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
    // ✅ Setup Handlers
    //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

    setupHandlers() {
      this._devicesSlider.$on('change', this.onDevicesSliderChange);
      this._dataSlider.$on('change', this.onDataSliderChange);
    }

    onDevicesSliderChange = (event) => {
      this._devicesValue.val(event.detail.value);
      this._devicesValueLabel.text(this.deviceCountFormatter(event.detail.value));
      this.findBestDataPlan();
    };

    onDataSliderChange = (event) => {
      this._dataValue.val(event.detail.value);
      this._dataValueLabel.text(this.dataFormatter(event.detail.value));
      this.findBestDataPlan();
    };

    getPricingOptionAnnualPrice = (pricing_option) => {
      const { annual_price, monthly_price } = pricing_option;
      let price = parseFloat(annual_price.replace('$', '')) || 0;
      if (price === 0 && monthly_price) {
        price = parseFloat(monthly_price.replace('$', '')) * 12 || 0;
      }
      return price;
    }

    findBestPricingOption = (pricing_options, totalData, numberOfDevices, dataPerDevice) => {
      let bestOption = null;

      // Check if any pricing option is "add-a-line" pricing
      const additionalLinePricing = pricing_options.find((option) => option.allowance.toLowerCase().includes('add-a-line'));

      for (const option of pricing_options) {
        const { allowance } = option;
        
        const optionAllowanceMB = this.convertToMB(allowance);
        const optionPricePerDevice = this.getPricingOptionAnnualPrice(option);
        let optionTotalPrice = optionPricePerDevice * numberOfDevices;

        let isUsingAdditionalLines = false;
        // If "add-a-line" pricing is available, check if plan meets or exceeds the total data needed
        if (additionalLinePricing && optionAllowanceMB >= totalData) {
          const additionalLinesTotalPrice = this.getPricingOptionAnnualPrice(additionalLinePricing) * (numberOfDevices - 1);
          const priceWithAdditionalLines = optionPricePerDevice + additionalLinesTotalPrice;
          // Is it cheaper to use the "add-a-line" pricing?
          if (priceWithAdditionalLines < optionTotalPrice) {
            optionTotalPrice = priceWithAdditionalLines;
            isUsingAdditionalLines = true;
          }
        }

        // Check if the option meets or exceeds the data needed
        if (optionAllowanceMB >= dataPerDevice) {
          // Check if this option has a lower annual price
          if (!bestOption || optionTotalPrice < bestOption.totalPrice) {
            bestOption = option;
            bestOption.totalPrice = optionTotalPrice;
            if (isUsingAdditionalLines) {
              bestOption.explanation = `A single ${bestOption.allowance} plan with ${numberOfDevices - 1} additional lines.`;
            } else {
              bestOption.explanation = `${numberOfDevices} devices, each with a ${bestOption.allowance} plan.`;
            }
          }
        }
      }

      return bestOption;
    }

    findBestDataPlan = () => {
      const dataPerDevice = this.toGigabytes(this._dataValue.val());
      const numberOfDevices = this.deviceCountFormatter(this._devicesValue.val());
      const totalData = dataPerDevice * numberOfDevices;

      // Convert the data to MB
      const dataPerDeviceMB = parseInt(dataPerDevice) / 1000;
      const totalDataMB = parseInt(totalData) / 1000;

      const dataPlans = this._plans;

      // Initialize variables to keep track of the best plan
      let bestPlan = null;
    
      // Iterate through the data plans
      for (const plan of dataPlans) {
        const { pricing_options } = plan;
        if (!pricing_options) continue;
    
        const bestOption = this.findBestPricingOption(pricing_options, totalDataMB, numberOfDevices, dataPerDeviceMB);
    
        // If a suitable option was found within this plan
        if (bestOption) {
          // Check if this plan has a lower annual price
          if (!bestPlan || bestOption.totalPrice < bestPlan.pricing.totalPrice) {
            bestPlan = { ...plan, pricing: bestOption };
            delete bestPlan.pricing_options;
          }
        }
      }

      if (bestPlan) {        
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0
        });
  
        const estimatedCost = formatter.format(bestPlan.pricing.totalPrice);
        this._estimatedCostLabel.text(estimatedCost ?? '');
        this._bestPlanLabel.text(bestPlan?.name ?? '');
        this._explanationLabel.text(bestPlan.pricing?.explanation ?? '');
        return bestPlan;
      } else {
        this._estimatedCostLabel.text('');
        this._bestPlanLabel.text('');
        this._explanationLabel.text('');
        return null;
      }
    }

    // Helper function to convert data allowances to MB
    convertToMB = (allowance) => {
      const lowerCaseAllowance = allowance.toLowerCase();
      if (lowerCaseAllowance.endsWith('kb')) {
        return parseInt(allowance) / 1000;
      } else if (lowerCaseAllowance.endsWith('mb')) {
        return parseInt(allowance);
      } else if (lowerCaseAllowance.endsWith('gb')) {
        return parseInt(allowance) * 1000;
      } else {
        return parseInt(allowance);
      }
    }

    deviceCountFormatter = (sliderPosition) => {
      sliderPosition = parseInt(sliderPosition);
      let value;
      if (sliderPosition <= 1) {
        value = 1;
      } else if (sliderPosition <= 10) {
        value = sliderPosition;
      } else if (sliderPosition <= 20) {
        // If its 11, then value = 15
        // Else if between 12 and 20, increment by 10
        // So 12 = 20, 13 = 30, 14 = 40, etc
        value = sliderPosition === 11 ? 15 : (sliderPosition - 10) * 10;
      } else if (sliderPosition <= 30) {
        value = sliderPosition === 21 ? 150 : (sliderPosition - 20) * 100;
      } else if (sliderPosition <= 40) {
        value = sliderPosition === 31 ? 1500 : (sliderPosition - 30) * 1000;
      } else {
        value = 10000;
      }

      return value;
    };

    // take the input value from the range, and then
    // power it so it outputs in a logarithmic scale
    toGigabytes = (value) => {
      if (value <= 10) return value;
      const MOD = (val, div) => val % div;
      const POW = (base, exp) => Math.pow(base, exp);
      const CEIL = (val) => Math.ceil(val);
      const MAX = (val, val2) => Math.max(val, val2);
      const MODULO = MAX(1, value - 1 - MOD(value - 11, 9));
      const POWER = POW(10, CEIL(MODULO / 10));
      return (value - MODULO) * POWER + POWER;
    };

    // format the value to a human readable string
    dataFormatter = (value) => {
      const gigabyte = 1000;
      return this.prettyBytes(this.toGigabytes(value) * gigabyte);
    };

    // #region pretty-bytes
    toLocaleString = (number, locale, options) => {
      let result = number;
      if (typeof locale === 'string' || Array.isArray(locale)) {
        result = number.toLocaleString(locale, options);
      } else if (locale === true || options !== undefined) {
        result = number.toLocaleString(undefined, options);
      }

      return result;
    };

    prettyBytes = (number, options) => {
      if (!Number.isFinite(number)) {
        throw new TypeError(`Expected a finite number, got ${typeof number}: ${number}`);
      }

      const BYTE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const BIBYTE_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
      const BIT_UNITS = ['b', 'kbit', 'Mbit', 'Gbit', 'Tbit', 'Pbit', 'Ebit', 'Zbit', 'Ybit'];
      const BIBIT_UNITS = ['b', 'kibit', 'Mibit', 'Gibit', 'Tibit', 'Pibit', 'Eibit', 'Zibit', 'Yibit'];

      options = {
        bits: false,
        binary: false,
        space: true,
        ...options,
      };

      const UNITS = options.bits
        ? options.binary
          ? BIBIT_UNITS
          : BIT_UNITS
        : options.binary
        ? BIBYTE_UNITS
        : BYTE_UNITS;

      const separator = options.space ? ' ' : '';

      if (options.signed && number === 0) {
        return ` 0${separator}${UNITS[0]}`;
      }

      const isNegative = number < 0;
      const prefix = isNegative ? '-' : options.signed ? '+' : '';

      if (isNegative) {
        number = -number;
      }

      let localeOptions;

      if (options.minimumFractionDigits !== undefined) {
        localeOptions = { minimumFractionDigits: options.minimumFractionDigits };
      }

      if (options.maximumFractionDigits !== undefined) {
        localeOptions = { maximumFractionDigits: options.maximumFractionDigits, ...localeOptions };
      }

      if (number < 1) {
        const numberString = this.toLocaleString(number, options.locale, localeOptions);
        return prefix + numberString + separator + UNITS[0];
      }

      const exponent = Math.min(
        Math.floor(options.binary ? Math.log(number) / Math.log(1024) : Math.log10(number) / 3),
        UNITS.length - 1
      );
      number /= (options.binary ? 1024 : 1000) ** exponent;

      if (!localeOptions) {
        number = number.toPrecision(3);
      }

      const numberString = this.toLocaleString(Number(number), options.locale, localeOptions);

      const unit = UNITS[exponent];

      return prefix + numberString + separator + unit;
    };

    // #endregion
  }

  //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  // ✅ Setup jQuery Plugin
  //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

  $.fn.cost_calculator = function () {
    var _ = this,
      opt = arguments[0],
      args = Array.prototype.slice.call(arguments, 1),
      l = _.length,
      i,
      ret;
    for (i = 0; i < l; i++) {
      if (typeof opt == 'object' || typeof opt == 'undefined') _[i].cost_calculator = new CostCalculator(_[i], opt);
      else ret = _[i].cost_calculator[opt].apply(_[i].cost_calculator, args);
      if (typeof ret != 'undefined') return ret;
    }
    return _;
  };

  //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡
  // ✅ Run
  //≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡≡

  $('.o-cost-calculator').cost_calculator();
});
